/**
 * PublicFooter — CMS-driven public footer (Round 40 · Task 7).
 *
 * Every field falls back to sane defaults so the footer always renders. CMS
 * keys used:
 *   * footer_org        — org name (heading), tagline (subheading), blurb (body)
 *   * footer_contact    — email (heading), phone (subheading), address (body)
 *   * footer_quick_links— label (heading) + pipe-separated links in body
 *   * footer_social     — label (heading) + pipe-separated links in body
 *   * footer_copyright  — main copyright (heading), short note (subheading)
 *                          - "{year}" placeholder is auto-substituted.
 *   * footer_disclaimer — legal disclaimer (body)
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { EnvelopeSimple, Phone, MapPin } from "@phosphor-icons/react";
import { parseLinks } from "./PublicNav";

export default function PublicFooter() {
  const [blocks, setBlocks] = useState({});

  useEffect(() => {
    const keys = ["footer_org", "footer_contact", "footer_quick_links", "footer_social", "footer_copyright", "footer_disclaimer"];
    Promise.all(keys.map((k) => api.get(`/site-content/${k}`).then((r) => [k, r.data]).catch(() => [k, null])))
      .then((rows) => setBlocks(Object.fromEntries(rows)));
  }, []);

  const org = blocks.footer_org;
  const contact = blocks.footer_contact;
  const quick = blocks.footer_quick_links;
  const social = blocks.footer_social;
  const copyright = blocks.footer_copyright;
  const disclaimer = blocks.footer_disclaimer;
  const year = new Date().getFullYear();
  const copyText = (copyright?.heading || "© {year} School Builder Lab Africa. All rights reserved.").replace("{year}", year);

  return (
    <footer className="bg-slate-950 text-slate-300 border-t border-slate-800" data-testid="public-footer">
      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-12 grid md:grid-cols-4 gap-8">
        <div className="md:col-span-2">
          <h3 className="text-white text-lg font-bold" style={{ fontFamily: "'Outfit', sans-serif" }}>
            {org?.heading || "School Builder Lab Africa"}
          </h3>
          <p className="mt-1 text-sm text-amber-400">{org?.subheading}</p>
          <p className="mt-3 text-sm text-slate-400 leading-relaxed whitespace-pre-wrap">{org?.body}</p>
        </div>

        {contact && (
          <div>
            <p className="uppercase tracking-widest text-[10px] text-slate-500 font-bold mb-3">Contact</p>
            {contact.heading && <p className="text-sm flex items-center gap-2"><EnvelopeSimple size={13} /> {contact.heading}</p>}
            {contact.subheading && <p className="text-sm mt-1 flex items-center gap-2"><Phone size={13} /> {contact.subheading}</p>}
            {contact.body && <p className="text-sm mt-1 flex items-center gap-2"><MapPin size={13} /> {contact.body}</p>}
          </div>
        )}

        {quick && (
          <div>
            <p className="uppercase tracking-widest text-[10px] text-slate-500 font-bold mb-3" data-testid="footer-quick-heading">{quick.heading || "Quick links"}</p>
            <ul className="space-y-1.5">
              {parseLinks(quick.body).map((l) => (
                <li key={l.label}>
                  <Link to={l.href} className="text-sm hover:text-white transition-colors">{l.label}</Link>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {social && parseLinks(social.body).length > 0 && (
        <div className="max-w-6xl mx-auto px-4 sm:px-8 pb-8 border-t border-slate-800 pt-6 flex flex-wrap gap-4">
          <p className="text-xs uppercase tracking-widest text-slate-500 font-bold mr-2">{social.heading || "Follow us"}:</p>
          {parseLinks(social.body).map((l) => (
            <a key={l.label} href={l.href} target="_blank" rel="noreferrer" className="text-sm text-amber-400 hover:text-amber-300">
              {l.label}
            </a>
          ))}
        </div>
      )}

      <div className="border-t border-slate-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
          <p className="text-xs text-slate-500" data-testid="footer-copyright">{copyText}</p>
          {copyright?.subheading && <p className="text-xs text-slate-600">{copyright.subheading}</p>}
        </div>
        {disclaimer?.body && (
          <div className="max-w-6xl mx-auto px-4 sm:px-8 pb-6">
            <p className="text-[11px] text-slate-600 leading-relaxed" data-testid="footer-disclaimer">{disclaimer.body}</p>
          </div>
        )}
      </div>
    </footer>
  );
}
